import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container, Card, Row, Col, Badge, Button,
  Alert, Spinner, Modal, Tab, Tabs, Form,
  ListGroup, OverlayTrigger, Tooltip, Toast
} from 'react-bootstrap';
import {
  FaStar, FaEdit, FaHistory,
  FaSignOutAlt, FaRegEdit, FaTrash, FaRedo,
  FaRegCheckCircle, FaRegTimesCircle, FaRegClock,
  FaRegCalendarAlt, FaMapMarkerAlt, FaUserCircle,
  FaBoxOpen, FaTruck, FaCheckCircle, FaTimesCircle
} from 'react-icons/fa';
import { motion } from 'framer-motion';
import apiClient from '../apis/apiClient';
import './Profile.css';
import { DateTime } from 'luxon';


const Profile = () => {
  const navigate = useNavigate();
  const [token, setToken] = useState(localStorage.getItem('authToken'));
  const [user, setUser] = useState(null);
  const [editProfile, setEditProfile] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [activeTab, setActiveTab] = useState('orders');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [addressToDelete, setAddressToDelete] = useState(null);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [orders, setOrders] = useState([]);
  const [addressForm, setAddressForm] = useState({
    type: 'Home',
    street: '',
    city: '',
    state: '',
    zipCode: '',
    phoneNumber: '',
    isDefault: false
  });
  const [isEditingAddress, setIsEditingAddress] = useState(false);
  const [currentAddressId, setCurrentAddressId] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [orderToCancel, setOrderToCancel] = useState(null);
  const [cancelReason, setCancelReason] = useState('');

  const isLoggedIn = !!token;
  const userId = localStorage.getItem('userId');
  const userEmail = localStorage.getItem('userEmail');
  const name = localStorage.getItem('userName');

  // Helper functions
  const formatPrice = (price) => {
    const num = Number(price) || 0;
    return num.toFixed(2);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Date not available';
    try {
      let dt;
      if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(dateString)) {
        dt = DateTime.fromFormat(dateString, 'yyyy-MM-dd HH:mm:ss', { zone: 'Europe/Berlin' });
      } else {
        dt = DateTime.fromISO(dateString, { zone: 'Europe/Berlin' });
      }

      if (!dt.isValid) {
        return dateString;
      }

      return dt.toLocaleString({ year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch {
      return dateString;
    }
  };

  const toggleOrderDetails = (orderId) => {
    setExpandedOrder(expandedOrder === orderId ? null : orderId);
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userId');
    localStorage.removeItem('userName');
    localStorage.removeItem('userEmail');
    setToken(null);
    setUser(null);
    navigate('/login');
  };

  // Profile and order fetching functions
  useEffect(() => {
    if (!isLoggedIn) {
      navigate('/login');
      return;
    }

    const fetchProfile = async () => {
      setLoading(true);
      try {
        const res = await apiClient.get(`/profiles/user/${userId}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (res.data) {
          setUser(res.data);
          setEditProfile({
            name: res.data.name || '',
            email: res.data.email || '',
            phone: res.data.phone || ''
          });
          setError('');
        } else {
          await createDefaultProfile();
        }
      } catch (err) {
        if (err.response?.status === 404) {
          await createDefaultProfile();
        } else {
          console.error("Profile fetch failed", err);
          setError(err.response?.data?.message || "Failed to load user profile");
          setUser(null);
        }
      } finally {
        setLoading(false);
      }
    };

    const createDefaultProfile = async () => {
      try {
        const userName = localStorage.getItem('userName') || 'New User';
        const userEmail = localStorage.getItem('userEmail') || '';

        if (!userEmail) {
          throw new Error("Email is required to create profile");
        }

        const res = await apiClient.post(
          `/profiles`,
          {
            userId: userId,
            name: userName,
            email: userEmail,
            phone: '',
            membership: 'Standard',
            addresses: []
          },
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        setUser(res.data);
        setEditProfile({
          name: res.data.name || '',
          email: res.data.email || '',
          phone: res.data.phone || ''
        });
        setSuccess('Default profile created successfully');
        setShowToast(true);
      } catch (createErr) {
        console.error("Profile creation failed", createErr);
        setError(createErr.response?.data?.message || createErr.message || "Failed to create user profile");
        setShowToast(true);
      }
    };

    fetchProfile();
  }, [isLoggedIn, token, navigate, userId]);

  const fetchOrders = async () => {
    if (!isLoggedIn || !userId) return;

    setLoading(true);
    try {
      const response = await apiClient.get(`/order/user/${userId}`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      const data = response.data;
      setOrders(Array.isArray(data) ? data : [data]);
    } catch (error) {
      console.error("Error fetching orders:", error);
      setError(error.response?.data?.message || error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [isLoggedIn, token, userId]);

  // Address management functions
  const deleteAddress = (id) => {
    setAddressToDelete(id);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    try {
      const res = await apiClient.delete(
        `/profiles/me/addresses/${addressToDelete}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      setUser(res.data); // assuming backend returns updated profile
      setSuccess('Address deleted successfully');
      setShowToast(true);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || "Failed to delete address");
      setShowToast(true);
    }
    setShowDeleteConfirm(false);
  };

  const setDefaultAddress = async (id) => {
    try {
      const res = await apiClient.patch(
        `/profiles/me/addresses/${id}/default`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );
      setUser(res.data); // Assuming it returns updated user info
      setSuccess('Default address updated successfully');
      setShowToast(true);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to set default address");
      setShowToast(true);
    }
  };


  // Profile edit functions
  const handleEditSave = async () => {
    try {
      const res = await apiClient.put(
        `/profiles/me`,
        editProfile,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      setUser(res.data);
      // Update localStorage with new name
      localStorage.setItem('userName', res.data.name);
      setShowEditModal(false);
      setSuccess('Profile updated successfully');
      setShowToast(true);
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || "Failed to update profile");
      setShowToast(true);
    }
  };

  // Address form functions
  const handleAddAddress = () => {
    setAddressForm({
      type: 'Home',
      street: '',
      city: '',
      state: '',
      zipCode: '',
      phoneNumber: '',
      isDefault: false
    });
    setIsEditingAddress(false);
    setCurrentAddressId(null);
    setShowAddressModal(true);
  };

  const handleEditAddress = (address) => {
    setAddressForm({
      type: address.type,
      street: address.streetAddress || address.street || '',
      city: address.city,
      state: address.state,
      zipCode: address.zipCode,
      phoneNumber: address.phoneNumber || '',
      isDefault: address.default || address.isDefault || false
    });
    setIsEditingAddress(true);
    setCurrentAddressId(address.id);
    setShowAddressModal(true);
  };

  const handleAddressSubmit = async (e) => {
    e.preventDefault();

    if (!addressForm.street || !addressForm.city || !addressForm.state || !addressForm.zipCode || !addressForm.phoneNumber) {
      setError('Please fill all required fields');
      setShowToast(true);
      return;
    }

    try {
      const addressPayload = {
        type: addressForm.type,
        streetAddress: addressForm.street,
        city: addressForm.city,
        state: addressForm.state,
        zipCode: addressForm.zipCode,
        phoneNumber: addressForm.phoneNumber,
        default: addressForm.isDefault
      };

      let res;

      if (isEditingAddress) {
        // Edit address (PUT)
        res = await apiClient.put(
          `/profiles/me/addresses/${currentAddressId}`,
          addressPayload,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
        setSuccess('Address updated successfully');
      } else {
        // Add new address (POST)
        res = await apiClient.post(
          '/profiles/addresses',
          addressPayload,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        );
        setSuccess('Address added successfully');
      }

      // Update state with response
      setUser(res.data);
      setShowAddressModal(false);
      setError('');
      setShowToast(true);

    } catch (err) {
      console.error('Address save error:', err.response?.data || err.message);
      setError(err.response?.data?.message || "Failed to save address");
      setShowToast(true);
    }
  };


  const handleAddressChange = (e) => {
    const { name, value, type, checked } = e.target;
    setAddressForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Order cancellation functions
  const handleCancelOrder = (order) => {
    setOrderToCancel(order);
    setCancelReason('');
    setShowCancelModal(true);
  };

  const confirmCancelOrder = async () => {
    if (!cancelReason) {
      setError('Please provide a reason for cancellation');
      setShowToast(true);
      return;
    }

    try {
      const updateResponse = await apiClient.patch(
        `/order/${orderToCancel.id}/status`,
        { status: 'CANCELLED' },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const cancelResponse = await apiClient.post(
        '/cancellations/cancel',
        {
          orderId: orderToCancel.id,
          reason: cancelReason
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (updateResponse.status === 200 && cancelResponse.status === 200) {
        await fetchOrders();
        setSuccess('Order cancelled successfully');
        setShowToast(true);
        setShowCancelModal(false);
      } else {
        throw new Error('Failed to cancel order');
      }
    } catch (err) {
      console.error('Error cancelling order:', err);
      setError(err.response?.data?.message || "Failed to cancel order");
      setShowToast(true);
    }
  };

  // UI helper functions
  const getStatusIcon = (status) => {
    const icons = {
      'DELIVERED': <FaCheckCircle className="text-success me-2" size={18} />,
      'CANCELLED': <FaTimesCircle className="text-danger me-2" size={18} />,
      'SHIPPED': <FaTruck className="text-primary me-2" size={18} />,
      'PROCESSING': <FaBoxOpen className="text-warning me-2" size={18} />,
      'PLACED': <FaRegClock className="text-info me-2" size={18} />,
      'default': <FaRegClock className="text-secondary me-2" size={18} />
    };
    return icons[status] || icons.default;
  };

  const getStatusBadge = (status) => {
    const colors = {
      'DELIVERED': 'success',
      'CANCELLED': 'danger',
      'SHIPPED': 'primary',
      'PROCESSING': 'warning',
      'PLACED': 'info',
      'default': 'secondary'
    };
    return colors[status] || colors.default;
  };

  const renderOrderItems = (items) => {
    if (!items || !Array.isArray(items)) {
      return (
        <ListGroup.Item>
          <div className="text-center text-muted py-3">
            Keine Artikel in dieser Bestellung gefunden
          </div>
        </ListGroup.Item>
      );
    }

    return items.map((item, index) => {
      const itemName = item.name || item.itemName || 'Unnamed Item';
      const itemPrice = item.price || item.unitPrice || 0;
      const itemQuantity = item.quantity || item.qty || 1;
      const itemId = item.id || item.dishId || item.dish?.dishid || index;

      return (
        <ListGroup.Item key={itemId} className="px-0">
          <Row className="align-items-center">
            <Col xs={3} md={2}>
              <div
                className="rounded overflow-hidden"
                style={{
                  width: '60px',
                  height: '60px',
                  background: '#f8f9fa',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <FaBoxOpen className="text-muted" />
              </div>
            </Col>
            <Col xs={6} md={7}>
              <h6 className="mb-1">{itemName}</h6>
              <div className="d-flex">
                <small className="text-muted me-2">Qty: {itemQuantity}</small>
                <small className="text-muted">Price: €{formatPrice(itemPrice)}</small>
              </div>
            </Col>
            <Col xs={3} md={3} className="text-end">
              <h6 className="mb-0">€{formatPrice(itemPrice * itemQuantity)}</h6>
            </Col>
          </Row>
        </ListGroup.Item>
      );
    });
  };

  const renderOrderSummary = (order) => {
    if (!order) return null;

    return (
      <div className="order-summary mt-3 pt-3 border-top">
        <Row>
          <Col md={6}>
            <Button
              variant="outline-danger"
              size="sm"
              disabled={order.status === 'CANCELLED' || order.status === 'DELIVERED'}
              onClick={() => handleCancelOrder(order)}
            >
              Bestellung stornieren
            </Button>
          </Col>
          <Col md={6}>
            <div className="text-md-end">
              <div className="d-flex justify-content-between justify-content-md-end">
                <span className="me-3">Zwischensumme:</span>
                <span>€{formatPrice(order.totalAmount)}</span>
              </div>
              <div className="d-flex justify-content-between justify-content-md-end">
                <span className="me-3">Gesamt:</span>
                <span className="fw-bold">€{formatPrice(order.totalAmount)}</span>
              </div>
            </div>
          </Col>
        </Row>
      </div>
    );
  };

  if (!isLoggedIn) {
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}>
        <Container className="profile-page py-5 text-center">
          <Row className="justify-content-center">
            <Col md={6}>
              <FaUserCircle size={100} className="text-primary mb-3" />
              <h2>Welcome to Papadam</h2>
              <p className="text-muted">Sign in to access your account</p>
              {error && <Alert variant="danger">{error}</Alert>}
              <Button variant="primary" onClick={() => navigate('/login')}>Sign In / Register</Button>
            </Col>
          </Row>
        </Container>
      </motion.div>
    );
  }

  if (loading && !user) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Laden Sie Ihr Profil...</p>
      </div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}>
      <Container className="profile-page py-4">
        <div style={{ position: 'fixed', top: '20px', right: '20px', zIndex: 9999 }}>
          <Toast
            onClose={() => setShowToast(false)}
            show={showToast}
            delay={3000}
            autohide
            bg={success ? 'success' : 'danger'}
          >
            <Toast.Header>
              <strong className="me-auto">{success ? 'Success' : 'Error'}</strong>
            </Toast.Header>
            <Toast.Body className="text-white">
              {success || error}
            </Toast.Body>
          </Toast>
        </div>

        <div className="profile-header mb-4">
          <Row className="align-items-center">
            <Col md={8}>
              <div className="d-flex align-items-center mb-3">
                <FaUserCircle size={60} className="text-primary me-3" />
                <div>
                  <div className="d-flex align-items-center">
                    <h2 className="mb-0 me-2">{user?.name || name}</h2>
                    <OverlayTrigger overlay={<Tooltip>Edit Profile</Tooltip>}>
                      <Button variant="link" size="sm" onClick={() => setShowEditModal(true)}>
                        <FaEdit size={16} />
                      </Button>
                    </OverlayTrigger>
                  </div>
                  {/* <Badge bg="warning" text="dark" className="me-2">{user?.membership || 'Standard'}</Badge> */}
                  {/* <small className="text-muted">
                    <FaRegCalendarAlt className="me-1" />{user?.joinDate || 'New member'}
                  </small> */}
                </div>
              </div>
            </Col>
            <Col md={4} className="text-md-end">
              <Button variant="outline-danger" onClick={handleLogout}>
                <FaSignOutAlt className="me-2" />
                Abmelden
              </Button>
            </Col>
          </Row>
        </div>

        {error && !showToast && <Alert variant="danger">{error}</Alert>}
        {success && !showToast && <Alert variant="success">{success}</Alert>}

        <Tabs activeKey={activeTab} onSelect={setActiveTab} className="mb-4" variant="pills">
          <Tab eventKey="orders" title={<><FaHistory className="me-1" /> Meine Bestellungen</>}>
            <Card className="mt-3 shadow-sm border-0">
              <Card.Body className="p-0">
                <div className="p-4 border-bottom">
                  <h5 className="mb-0">Bestellhistorie</h5>
                </div>

                {loading && orders.length === 0 ? (
                  <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                  </div>
                ) : orders.length === 0 ? (
                  <div className="text-center py-5">
                    <img
                      src="https://cdn-icons-png.flaticon.com/512/4555/4555971.png"
                      alt="No orders"
                      style={{ width: '120px', opacity: 0.7 }}
                      className="mb-3"
                    />
                    <h5 className="text-muted">Noch keine Bestellungen</h5>
                    <p className="text-muted">Ihre Bestellhistorie wird hier angezeigt</p>
                    <Button variant="primary" onClick={() => navigate('/Orders')}>
                      Beginnen Sie mit der Bestellung
                    </Button>
                  </div>
                ) : (
                  <div className="order-list">
                    {orders.map(order => (
                      <div key={order.id} className="order-item border-bottom p-4">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <div>
                            <h6 className="mb-1">Befehl #{order.id}</h6>
                            <small className="text-muted">
                              <FaRegCalendarAlt className="me-2" />
                              {formatDate(order.orderDate)}
                            </small>
                          </div>
                          <Badge bg={getStatusBadge(order.status)} className="px-3 py-2" style={{ marginLeft: "50px" }}>
                            {getStatusIcon(order.status)}
                            <span style={{ paddingRight: "20px" }}>{order.status}</span>
                          </Badge>
                        </div>

                        <Row className="align-items-center mb-3">
                          <Col md={6}>
                            <div className="d-flex align-items-center">
                              <div className="me-3">
                                <h5 className="mb-0">€{formatPrice(order.totalAmount)}</h5>
                                <small className="text-muted">
                                  {(order.orderItems?.length) || 0} items
                                </small>
                              </div>
                              {order.items?.slice(0, 3).map((item, idx) => (
                                <div
                                  key={idx}
                                  className="me-2 rounded overflow-hidden"
                                  style={{
                                    width: '50px',
                                    height: '50px',
                                    background: '#f8f9fa',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}
                                >
                                  <FaBoxOpen className="text-muted" />
                                </div>
                              ))}
                              {order.items?.length > 3 && (
                                <div className="ms-2">
                                  +{order.items.length - 3} more
                                </div>
                              )}
                            </div>
                          </Col>
                          <Col md={6} className="text-md-end mt-2 mt-md-0">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => toggleOrderDetails(order.id)}
                              className="me-2"
                            >
                              {expandedOrder === order.id ? 'Details ausblenden' : 'Details anzeigen'}
                            </Button>
                          </Col>
                        </Row>

                        {expandedOrder === order.id && (
                          <div className="order-details mt-3 pt-3 border-top">
                            <h6 className="mb-3">Bestelldetails</h6>
                            <Row>
                              <Col md={6}>
                                <div className="mb-3">
                                  <h6>Lieferadresse</h6>
                                  {order.shippingAddress ? (
                                    <div className="text-muted">
                                      <p className="mb-1">{order.shippingAddress.street || order.shippingAddress.streetAddress}</p>
                                      <p className="mb-1">{order.shippingAddress.city}, {order.shippingAddress.state}</p>
                                      <p className="mb-1">{order.shippingAddress.zipCode}</p>
                                      {order.shippingAddress.phoneNumber && (
                                        <p className="mb-1">📱 {order.shippingAddress.phoneNumber}</p>
                                      )}
                                    </div>
                                  ) : (
                                    <p className="text-muted">Keine Lieferadresse angegeben</p>
                                  )}
                                </div>
                              </Col>
                            </Row>

                            <div className="order-items">
                              <h6 className="mb-3">Artikel</h6>
                              <ListGroup variant="flush">
                                {renderOrderItems(order.orderItems)}
                              </ListGroup>
                            </div>

                            {renderOrderSummary(order)}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </Card.Body>
            </Card>
          </Tab>

          <Tab eventKey="addresses" title={<><FaMapMarkerAlt className="me-1" />Meine Adressen</>}>
            <Card className="mt-3 shadow-sm">
              <Card.Body>
                <div className="d-flex justify-content-between mb-4 adreess">
                  <h5>Gespeicherte Adressen</h5>
                  <Button variant="primary" size="sm" onClick={handleAddAddress}>Neue Adresse hinzufügen</Button>
                </div>
                <Row className="g-3">
                  {(user?.addresses || []).map(address => (
                    <Col md={6} key={address.id}>
                      <Card>
                        <Card.Body>
                          <div className="d-flex justify-content-between">
                            <div>
                              <h6 className="mb-2 ad2">
                                {address.type}
                                {address.default && <Badge bg="success" pill className="ms-2">Default</Badge>}
                              </h6>
                              <p>{address.streetAddress || address.street}</p>
                              <p>{address.city}, {address.state} {address.zipCode}</p>
                            </div>
                            <div className="d-flex flex-column">
                              <Button
                                variant="outline-secondary"
                                size="sm"
                                className="mb-2"
                                onClick={() => handleEditAddress(address)}
                              >
                                <FaRegEdit />
                              </Button>
                              {!address.default && (
                                <>
                                  <Button
                                    variant="outline-success"
                                    size="sm"
                                    className="mb-2"
                                    onClick={() => setDefaultAddress(address.id)}
                                  >
                                    Set Default
                                  </Button>
                                  <Button
                                    variant="outline-danger"
                                    size="sm"
                                    onClick={() => deleteAddress(address.id)}
                                  >
                                    <FaTrash />
                                  </Button>
                                </>
                              )}
                            </div>
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  ))}
                </Row>
              </Card.Body>
            </Card>
          </Tab>
        </Tabs>

        {/* Edit Profile Modal */}
        <Modal show={showEditModal} onHide={() => setShowEditModal(false)} centered>
          <Modal.Header closeButton><Modal.Title>Profil bearbeiten</Modal.Title></Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Vollständiger Name</Form.Label>
                <Form.Control
                  type="text"
                  value={editProfile.name || ''}
                  onChange={(e) => setEditProfile({ ...editProfile, name: e.target.value })}
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>E-Mail</Form.Label>
                <Form.Control
                  type="email"
                  value={editProfile.email || ''}
                  onChange={(e) => setEditProfile({ ...editProfile, email: e.target.value })}
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Telefon</Form.Label>
                <Form.Control
                  type="tel"
                  value={editProfile.phone || ''}
                  onChange={(e) => setEditProfile({ ...editProfile, phone: e.target.value })}
                />
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setShowEditModal(false)}>Stornieren</Button>
            <Button variant="primary" onClick={handleEditSave}>Speichern</Button>
          </Modal.Footer>
        </Modal>

        {/* Address Modal */}
        <Modal show={showAddressModal} onHide={() => setShowAddressModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>{isEditingAddress ? 'Edit Address' : 'Neue Adresse hinzufügen'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form onSubmit={handleAddressSubmit}>
              <Form.Group className="mb-3">
                <Form.Label>Adresstyp</Form.Label>
                <Form.Select
                  name="type"
                  value={addressForm.type}
                  onChange={handleAddressChange}
                  required
                >
                  <option value="Home">Heim</option>
                  <option value="Work">Arbeiten</option>
                  <option value="Other">Andere</option>
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Straßenadresse*</Form.Label>
                <Form.Control
                  type="text"
                  name="street"
                  value={addressForm.street}
                  onChange={handleAddressChange}
                  required
                />
              </Form.Group>

              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Stadt*</Form.Label>
                    <Form.Control
                      type="text"
                      name="city"
                      value={addressForm.city}
                      onChange={handleAddressChange}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Zustand*</Form.Label>
                    <Form.Control
                      type="text"
                      name="state"
                      value={addressForm.state}
                      onChange={handleAddressChange}
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Label>PLZ*</Form.Label>
                <Form.Control
                  type="text"
                  name="zipCode"
                  value={addressForm.zipCode}
                  onChange={handleAddressChange}
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Telefonnummer*</Form.Label>
                <Form.Control
                  type="tel"
                  name="phoneNumber"
                  value={addressForm.phoneNumber}
                  onChange={handleAddressChange}
                  placeholder="+49 123 456 7890"
                  required
                />
              </Form.Group>

              <Form.Check
                type="checkbox"
                label="Als Standardadresse festlegen"
                name="isDefault"
                checked={addressForm.isDefault}
                onChange={handleAddressChange}
              />
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setShowAddressModal(false)}>Stornieren</Button>
            <Button variant="primary" onClick={handleAddressSubmit}>
              {isEditingAddress ? 'Adresse aktualisieren' : 'Adresse speichern'}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Delete Address Modal */}
        <Modal show={showDeleteConfirm} onHide={() => setShowDeleteConfirm(false)} centered>
          <Modal.Header closeButton><Modal.Title>Bestätigen Sie den Löschvorgang</Modal.Title></Modal.Header>
          <Modal.Body>Möchten Sie diese Adresse wirklich löschen??</Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setShowDeleteConfirm(false)}>Stornieren</Button>
            <Button variant="danger" onClick={confirmDelete}>Löschen</Button>
          </Modal.Footer>
        </Modal>

        {/* Cancel Order Modal */}
        <Modal show={showCancelModal} onHide={() => setShowCancelModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Bestellung stornieren #{orderToCancel?.id}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Grund für die Stornierung</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  placeholder="Please provide a reason for cancelling this order..."
                  required
                />
              </Form.Group>
              <Alert variant="warning">
                <FaRegTimesCircle className="me-2" />
                Hinweis: Nach dem Abbruch kann diese Aktion nicht mehr rückgängig gemacht werden.
              </Alert>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setShowCancelModal(false)}>
              Go Back
            </Button>
            <Button variant="danger" onClick={confirmCancelOrder}>
              Stornierung bestätigen
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </motion.div>
  );
};

export default Profile;

